<template>
  <v-card
    :height="widgetHeight"
    :min-width="widgetWidth"
    class="ma-0 pa-0"
    :loading="loading"
    @mouseover.native="hover = true"
    @mouseleave.native="hover = false"
  >
    <v-card-title
      v-if="!loading"
      class="pa-0 ma-0"
      style="max-height: 40px !important"
    >
      <widget-header
        :title="
          documentTrackerSettings.title || $t('WidgetDocumentTrackerTitle')
        "
        :color="documentTrackerSettings.color || '#607d8b'"
        icon="mdi-calendar-month"
      >
        <template slot="actions">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="refresh"
                >mdi-refresh</v-icon
              >
            </template>
            <span>{{ $t("Refresh") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="editSettings"
                >mdi-cog</v-icon
              >
            </template>
            <span>{{ $t("Settings") }}</span>
          </v-tooltip>
          <v-tooltip top v-if="enableEdit">
            <template v-slot:activator="{ on }">
              <v-icon
                v-on="on"
                class="mr-2"
                @click.prevent="removeWidget"
                >mdi-delete</v-icon
              >
            </template>
            <span>{{ $t("Delete") }}</span>
          </v-tooltip>
        </template>
      </widget-header>
    </v-card-title>
    <v-card-text v-if="!loading"
      class="pa-3 ma-0 overflow-y-auto"
      :style="`height: ${contentHeight}px;max-height: ${contentHeight}px`"
    >
      <v-row dense>
        <v-col @click="showData(documentTrackerStates.Expired)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-alert"
                    :title="expiredDocuments.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.expiredColor"
                    :textColor="documentTrackerSettings.expiredTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{ $t("ExpiredPlural") }}</span>
          </v-tooltip>
        </v-col>
        <v-col @click="showData(documentTrackerStates.Warning)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-clock"
                    :title="warningDocuments.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.warningColor"
                    :textColor="documentTrackerSettings.warningTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{
              `${$t("ExpireInLessThan")} ${
                documentTrackerSettings.warningDays
              } ${$t("Days")}`
            }}</span>
          </v-tooltip>
        </v-col>
        <v-col @click="showData(documentTrackerStates.OnTime)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <div v-on="on">
                <v-hover v-slot="{ hover }">
                  <MiniStatistic
                    :elevation="hover ? 4 : 2"
                    :class="hover ? 'pointer' : ''"
                    icon="mdi-calendar-check"
                    :title="okDocuments.toString()"
                    sub-title=" "
                    :color="documentTrackerSettings.onTimeColor"
                    :textColor="documentTrackerSettings.onTimeTextColor"
                    iconSize="48"
                  />
                </v-hover>
              </div>
            </template>
            <span>{{ $t('Site.Widgets.WidgetDocumentTracker.UpToDate') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <SettingsModal :ref="settingsModalKey" @on-save="onSettingsSave" />
      <DataModal
        ref="modalTrackerDataKey"
        :widgetTitle="
          documentTrackerSettings.title || $t('WidgetDocumentTrackerTitle')
        "
        @on-documents-validated="updateData(true)"
        @on-documents-updated="updateData"
        :warningDays="documentTrackerSettings.warningDays"
      />
    </v-card-text>
  </v-card>
</template>

<script>
import SettingsModal from '@/components/widgets/documentTracker/WidgetDocumentTrackerSettings';
import DocumentTrackerSettings from '@/ecm/models/widget/documentTracker/DocumentTrackerSettings';
import DataModal from '@/components/widgets/documentTracker/WidgetDocumentTrackerDataModal';
import WidgetHeader from '@/components/widgets/WidgetHeader';
import MiniStatistic from '@/components/widgets/statistic/MiniStatistic';
import WidgetService from '@/services/WidgetService';
import { mapGetters } from 'vuex';
import UserWidgetService from '@/services/UserWidgetService';
import { EventBus } from '@/eventBus';
import { WidgetDocumentTrackerStates } from '@/ecm/variables/WidgetDocumentTrackerStates';

export default {
    components: {
        SettingsModal,
        WidgetHeader,
        MiniStatistic,
        DataModal,
    },
    props: {
        widgetId: {
            type: Number,
            default: 0,
        },
        id: {
            type: Number,
            default: 0,
        },
        enableEdit: {
            type: Boolean,
            default: false,
        },
        height: { type: Number, default: 0 },
        width: { type: Number, default: 0 },
        settings: { type: Object },
    },
    mounted() {
        this.widgetHeight = this.height;
        this.widgetWidth = this.width;
        this.documentTrackerSettings = new DocumentTrackerSettings(this.settings);
        this.refresh();

        this.connection = $.hubConnection(this.apiUrl);
        this.connection.qs = { access_token: this.token };
        this.notificationHub = this.connection.createHubProxy('notificationHub');
        const self = this;

        this.onRefresh = (notifications) => {
            const now = Date.now();
            const timeSinceLastMessage = now - self.lastMessageTime;

            // Clear any existing timeout
            if (self.refreshTimeout) {
                clearTimeout(self.refreshTimeout);
                self.refreshTimeout = null;
            }

            // Determine if messages are coming rapidly
            if (timeSinceLastMessage < self.rapidThreshold && self.lastMessageTime > 0) {
                // Messages are rapid - increase the delay
                self.messageCount++;
            } else {
                // Messages have slowed down or this is the first message
                self.messageCount = 1;
            }

            self.lastMessageTime = now;

            // Calculate adaptive delay based on message frequency
            // The more messages in quick succession, the longer we wait
            let delay = self.baseDelay;
            if (self.messageCount > 1) {
                // Exponential backoff: 1s, 2s, 4s, 8s, up to maxDelay
                delay = Math.min(
                    self.baseDelay * Math.pow(2, self.messageCount - 1),
                    self.maxDelay
                );
            }

            // Schedule the refresh
            self.refreshTimeout = setTimeout(() => {
                self.refresh();
                // Reset the message count after refresh
                self.messageCount = 0;
                self.refreshTimeout = null;
            }, delay);
        };

        this.notificationHub.off('refresh', this.onRefresh);
        this.notificationHub.on('refresh', this.onRefresh);

        // Handle SignalR events for connection status
        this.onReconnected = () => { self.refresh(); };
        this.onDisconnected = () => { self.tryReconnect(); };
        this.onError = (ex) => { console.error('SignalR error:', ex); };

        this.connection.reconnected(this.onReconnected);
        this.connection.disconnected(this.onDisconnected);
        this.connection.error(this.onError);

        this.connection.start();
    },
    beforeDestroy() {
        // Clear any pending refresh timeout
        if (this.refreshTimeout) {
            clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }

        if (this.notificationHub && this.connection) {
            if (this.onRefresh) this.notificationHub.off('refresh');

            const $c = $(this.connection);

            $c.off('onReconnect');
            $c.off('onDisconnect');
            $c.off('onError');

            this.connection.stop();
            this.connection = null;
        }
    },
    data() {
        return {
            // SignalR
            connection: null,
            notificationHub: null,
            onRefresh: null,
            onReconnecting: null,
            onDisconnected: null,
            onError: null,
            signalRIsTryingToReconnect: false,
            // Adaptive refresh control
            refreshTimeout: null,
            messageCount: 0,
            lastMessageTime: 0,
            baseDelay: 1000, // Base delay: 1 second
            maxDelay: 10000, // Maximum delay: 10 seconds
            rapidThreshold: 500, // If messages come within 500ms, consider it rapid

            hover: false,
            headerHeight: 30,
            widgetHeight: 0,
            widgetWidth: 0,
            loading: false,
            documentTrackerSettings: new DocumentTrackerSettings(),
            settingsModalKey: 'modalSettingsKey',
            dbTableName: 'widgetDocumentTracker',
            expiredDocuments: 0,
            warningDocuments: 0,
            okDocuments: 0,
            documentTrackerStates: WidgetDocumentTrackerStates,
        };
    },
    computed: {
        ...mapGetters({
            user: 'account/user',
            apiUrl: 'account/apiUrl',
            token: 'account/token',
            userContentTypes: 'account/userContentTypes',
        }),
        contentHeight() {
            return this.widgetHeight - this.headerHeight;
        },
    },
    methods: {
        resize(height, width) {
            this.widgetHeight = height;
            this.widgetWidth = width;
        },
        editSettings() {
            this.$refs[this.settingsModalKey].edit(this.documentTrackerSettings);
        },
        async showData(state, refreshData) {
            this.documentTrackerSettings.selectedDocumentState = state;
            await this.$refs['modalTrackerDataKey'].showData(this.documentTrackerSettings.toDto(), state, refreshData);
        },
        async onSettingsSave(settings) {
            const widgetDto = {
                Id: this.id,
                WidgetID: this.widgetId,
                Settings: settings.toDto(),
            };

            await UserWidgetService.updateSettings(widgetDto);
            this.documentTrackerSettings = settings;
            await this.refresh();
        },
        removeWidget() {
            this.$emit('on-remove-widget', this.id);
        },
        async refresh() {
            this.loading = true;
            await this.getData();
            this.loading = false;
        },
        async updateData(keepDataModalOpen) {
            this.loading = true;
            await this.getData();
            if (keepDataModalOpen) {
                await this.showData(this.documentTrackerSettings.selectedDocumentState, true);
            }
            this.loading = false;
        },
        async getData() {
            const data = await WidgetService.getData({
                WidgetID: this.widgetId,
                Settings: this.documentTrackerSettings.toDto(),
            });
            this.expiredDocuments = data.ExpiredDocuments;
            this.warningDocuments = data.WarningDocuments;
            this.okDocuments = data.OkDocuments;
        },
        tryReconnect() {
            if (this.signalRIsTryingToReconnect)
                return;

            let attempts = 0;
            const maxAttempts = 5;
            const self = this;
            this.signalRIsTryingToReconnect = true;

            function reconnect() {
                if (attempts < maxAttempts) {
                    self.connection.start().done(() => {
                        self.signalRIsTryingToReconnect = false;
                        EventBus.$emit('connection-status', null);
                    }).fail(() => {
                        attempts++;
                        setTimeout(reconnect, 5000); // Retry after 5 seconds
                    });
                } else {
                    console.log('Max reconnection attempts reached. Could not reconnect to SignalR.');
                }
            }

            reconnect();
        }
    },
};
</script>
<style>
.pointer {
  cursor: pointer;
}
</style>
